import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Check, ChevronDown, Flame, Loader2 } from 'lucide-react';
import { AutoTickets, BuilderBar } from './CfbTicketBuilder';
import { CfbGameDetail } from './CfbGameDetail';
import { CfbMatchup, briefLine, projectedScore } from './CfbMatchup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const TZ = 'America/Los_Angeles';
export const FOR_SURE = 0.8;

const dayOf = (iso) => new Date(iso).toLocaleDateString('en-US', { timeZone: TZ, weekday: 'short' });
const kick = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: TZ, weekday: 'short', hour: 'numeric', minute: '2-digit' });
const rk = (t) => (t?.rank ? `#${t.rank} ` : '');

const Info = ({ g }) => {
  const p = g.pick;
  const me = g.teams.find((t) => t.abbr === p.abbr);
  const opp = g.teams.find((t) => t.abbr !== p.abbr);
  const ps = projectedScore(g);
  return (
    <span className="block text-[11px] text-zinc-400 leading-snug">
      <span className="block" data-testid={`cfb-board-records-${g.event_id}`}>
        <span className="text-white font-semibold">{rk(me)}{p.abbr} {me?.record}</span> {me?.home_away === 'home' ? 'vs' : '@'} <span className="text-zinc-300">{rk(opp)}{opp?.abbr} {opp?.record}</span>
        <span className="text-zinc-500"> · {g.state === 'in' ? <span className="text-red-400 font-semibold">LIVE</span> : kick(g.date)}</span>
      </span>
      <span className="block">
        <span className="whitespace-nowrap">ML {p.ml && p.ml !== 'OFF' ? p.ml : 'not offered'}</span>
        {ps && <span className="text-zinc-300" data-testid={`cfb-board-proj-${g.event_id}`}> · Projected <b className="text-white">{ps.me} {ps.a}</b>–{ps.op} {ps.b}</span>}
      </span>
      <span className="block text-zinc-500 line-clamp-1" data-testid={`cfb-board-brief-${g.event_id}`}>{briefLine(g)}</span>
      <TeamFacts t={me} id={`${g.event_id}-me`} />
      <TeamFacts t={opp} id={`${g.event_id}-opp`} />
      {p.source === 'fpi' && <span className="block text-[10px] text-sky-300" data-testid={`cfb-board-fpi-only-${g.event_id}`}>No sportsbook line yet — win chance from ESPN's prediction</span>}
    </span>
  );
};

// Home/road record, scoring and last 3 results for one team.
const TeamFacts = ({ t, id }) => {
  if (!t) return null;
  const f = t.form || {};
  const bits = [t.home_record && `Home ${t.home_record}`, t.away_record && `Road ${t.away_record}`,
    f.ppg != null && `${f.ppg} pts/g, allows ${f.papg}`, f.last3?.length && `Last 3: ${f.last3.join(', ')}`].filter(Boolean);
  if (!bits.length) return null;
  return <span className="block text-[10px] text-zinc-500 leading-snug" data-testid={`cfb-board-facts-${id}`}><b className="text-zinc-300">{t.abbr}</b> · {bits.join(' · ')}</span>;
};

const Logo = ({ g }) => {
  const me = g.teams.find((t) => t.abbr === g.pick.abbr);
  return (
    <span className="relative shrink-0">
      {g.pick.logo && <img src={g.pick.logo} alt="" className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/90 p-[2px] object-contain" />}
      {me?.rank && <span className="absolute -top-1.5 -right-2 rounded-full bg-yellow-400 px-1 text-[9px] font-black text-black leading-4" data-testid={`cfb-board-rank-${g.event_id}`}>#{me.rank}</span>}
    </span>
  );
};

const GameRow = ({ g, rank, picked, onToggle, open, onOpen }) => {
  const p = g.pick;
  const pct = Math.min(99, Math.round(p.prob * 100));
  const sure = pct >= FOR_SURE * 100;
  return (
    <div className={`rounded-lg mb-1 border transition-colors ${sure ? 'border-emerald-400/60 bg-emerald-500/10 shadow-[0_0_14px_rgba(52,211,153,0.18)]' : 'border-zinc-800 bg-zinc-950/40'}`}>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button type="button" onClick={() => onToggle(g)} aria-pressed={picked} data-testid={`cfb-board-game-${g.event_id}`} className="flex items-center gap-2.5 flex-1 min-w-0 text-left">
          <span className={`w-6 h-6 shrink-0 rounded-full border flex items-center justify-center text-[10px] font-mono ${picked ? 'bg-emerald-400 border-emerald-400 text-black' : 'border-zinc-700 text-zinc-400'}`}>{picked ? <Check className="w-3.5 h-3.5" /> : rank}</span>
          <Logo g={g} />
          <span className="block min-w-0 flex-1">
            <span className="block text-[13px] md:text-base font-bold text-white leading-tight" data-testid={`cfb-board-pick-${g.event_id}`}>
              {p.team} <span className="font-normal text-zinc-400 text-xs">to win</span> {sure && <span className="ml-1 align-middle whitespace-nowrap rounded bg-emerald-400 px-1.5 py-[1px] text-[9px] font-black tracking-wide text-black" data-testid={`cfb-board-sure-${g.event_id}`}>FOR SURE</span>}
            </span>
            <Info g={g} />
          </span>
          <span className="block w-12 md:w-28 shrink-0" data-testid={`cfb-board-conf-${g.event_id}`}>
            <span className={`flex justify-end font-mono text-sm font-bold ${sure ? 'text-emerald-300' : 'text-zinc-300'}`}>{pct}%</span>
            <span className="hidden md:flex justify-end text-[9px] text-zinc-500 -mt-0.5 mb-0.5">win outright</span>
            <span className="block h-1.5 rounded-full bg-zinc-800 overflow-hidden"><span className={`block h-full ${sure ? 'bg-emerald-400' : 'bg-zinc-500'}`} style={{ width: `${pct}%` }} /></span>
          </span>
        </button>
        <button type="button" onClick={() => onOpen(g.event_id)} aria-label="Details" data-testid={`cfb-board-details-${g.event_id}`}
          className="shrink-0 rounded-full p-1.5 text-zinc-400 hover:bg-zinc-800 transition-colors">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (<><CfbMatchup g={g} /><CfbGameDetail g={g} /></>)}
    </div>
  );
};

export default function CfbBlowoutBoard() {
  const [games, setGames] = useState(null);
  const [day, setDay] = useState('Sat');
  const [picked, setPicked] = useState([]);
  const [open, setOpen] = useState(null);
  const [stake, setStake] = useState(250);
  useEffect(() => {
    axios.get(`${API}/board`).then((r) => setGames(r.data?.cfb?.games || [])).catch(() => setGames([]));
  }, []);
  const inDay = (g) => (day === 'next' ? g.next_week : !g.next_week && (day === 'all' || dayOf(g.date) === day));
  const list = useMemo(() => (games || []).filter((g) => g.pick && inDay(g))
    .sort((a, b) => b.pick.prob - a.pick.prob), [games, day]);
  const toggle = (g) => setPicked((p) => (p.includes(g.event_id) ? p.filter((x) => x !== g.event_id) : [...p, g.event_id]));
  const selected = list.filter((g) => picked.includes(g.event_id));
  const sureCount = list.filter((g) => Math.round(g.pick.prob * 100) >= FOR_SURE * 100).length;
  return (
    <section className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 md:p-4" data-testid="cfb-blowout-board">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Flame className="w-5 h-5 text-orange-400" />
        <h2 className="text-base md:text-lg font-bold flex-1">Straight win picks · most → least likely</h2>
        {[['Sat', 'Saturday'], ['all', 'All week'], ['next', 'Next week']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => { setDay(v); setPicked([]); }} data-testid={`cfb-board-day-${v}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${day === v ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>{l}</button>
        ))}
      </div>
      {games === null ? (
        <div className="flex items-center gap-2 text-sm text-zinc-400 py-6 justify-center" data-testid="cfb-board-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading games…</div>
      ) : !list.length ? (
        <p className="text-sm text-zinc-500 py-6 text-center" data-testid="cfb-board-empty">No college games with odds yet for this day.</p>
      ) : (
        <div className="space-y-3">
          <AutoTickets games={list} stake={stake} setStake={setStake} onUse={(t) => setPicked(t.map((g) => g.event_id))} />
          <p className="text-[11px] text-zinc-500" data-testid="cfb-board-legend">
            <span className="text-emerald-300 font-semibold">{sureCount} FOR SURE</span> picks (80%+ chance to win outright) are lit up · {list.length} games · every pick is a straight win, no spreads · tap a game to add it to your own ticket, tap the arrow for the full breakdown.
          </p>
          <div data-testid="cfb-board-list">
            {list.map((g, i) => (
              <div key={g.event_id}>
                {i === sureCount && sureCount > 0 && <p className="text-[11px] text-zinc-500 py-2 text-center" data-testid="cfb-board-divider">— below 80%: less sure —</p>}
                <GameRow g={g} rank={i + 1} picked={picked.includes(g.event_id)} onToggle={toggle} open={open === g.event_id} onOpen={(id) => setOpen(open === id ? null : id)} />
              </div>
            ))}
          </div>
          <BuilderBar selected={selected} stake={stake} onClear={() => setPicked([])} />
        </div>
      )}
    </section>
  );
}
